import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ActivityItem from './components/ActivityItem';
import { useAuth } from '../../contexts/AuthContext';
import { activityService } from '../../services/supabaseService';

const History = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadActivities();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadActivities = async () => {
    setLoading(true);
    const { data } = await activityService?.getByUser(user?.id);
    setActivities(data || []);
    setLoading(false);
  };

  // Group activities by date
  const groupedActivities = activities?.reduce((groups, activity) => {
    const date = new Date(activity?.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);

    let dateLabel;
    if (date?.toDateString() === today?.toDateString()) {
      dateLabel = `Today — ${today?.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (date?.toDateString() === yesterday?.toDateString()) {
      dateLabel = `Yesterday — ${yesterday?.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else {
      dateLabel = date?.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    if (!groups?.[dateLabel]) groups[dateLabel] = [];
    groups?.[dateLabel]?.push({
      id: activity?.id,
      type: activity?.activity_type,
      title: activity?.title,
      time: date?.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
      detail: activity?.detail,
      tags: activity?.tags || []
    });
    return groups;
  }, {});

  const activityGroups = Object.entries(groupedActivities)?.map(([date, acts]) => ({ date, activities: acts }));

  const allActivities = activities;
  const totalSearches = allActivities?.filter(a => a?.activity_type === 'search')?.length;
  const totalAnalyses = allActivities?.filter(a => a?.activity_type === 'analysis')?.length;
  const totalViewed = allActivities?.filter(a => a?.activity_type === 'view')?.length;

  const filteredGroups = activityGroups?.map(group => ({
    ...group,
    activities: group?.activities?.filter(a => {
      const matchesFilter = filter === 'all' || a?.type === filter;
      const matchesSearch = !search?.trim() || a?.title?.toLowerCase()?.includes(search?.toLowerCase()) || a?.detail?.toLowerCase()?.includes(search?.toLowerCase());
      return matchesFilter && matchesSearch;
    })
  }))?.filter(g => g?.activities?.length > 0);

  const filterOptions = [
    { value: 'all', label: 'All Activity' },
    { value: 'search', label: 'Searches' },
    { value: 'analysis', label: 'Analyses' },
    { value: 'view', label: 'Viewed' },
    { value: 'save', label: 'Saved' },
    { value: 'compare', label: 'Comparisons' },
    { value: 'report', label: 'Reports' },
  ];

  const headerUser = user ? {
    id: user?.id,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email,
    role: userProfile?.role || 'buyer'
  } : null;

  return (
    <div className="min-h-screen bg-background">
      <Header user={headerUser} onLogout={async () => { await signOut(); navigate('/authentication'); }} />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Home
            </button>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)' }}>
                    <Icon name="History" size={20} color="#00D4FF" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">Activity History</h1>
                </div>
                <p className="text-muted-foreground">Your complete property search journey — searches, analyses, and saved properties.</p>
              </div>
              <button onClick={() => { setFilter('all'); setSearch(''); }} className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-border hover:bg-muted transition-colors text-muted-foreground">
                <Icon name="RotateCcw" size={14} color="currentColor" />
                Clear Filters
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Searches', value: totalSearches, icon: 'Search', color: '#00D4FF' },
              { label: 'Analyses', value: totalAnalyses, icon: 'BarChart2', color: '#A78BFA' },
              { label: 'Viewed', value: totalViewed, icon: 'Eye', color: '#34D399' },
            ]?.map(stat => (
              <div key={stat?.label} className="rounded-xl p-4 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Icon name={stat?.icon} size={20} color={stat?.color} className="mx-auto mb-2" />
                <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                <p className="text-xs text-muted-foreground">{stat?.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {filterOptions?.map(opt => (
              <button
                key={opt?.value}
                onClick={() => setFilter(opt?.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === opt?.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt?.label}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Search activity..."
              className="w-full px-4 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : filteredGroups?.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="History" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-30" />
              <p className="text-foreground font-semibold mb-2">No activity found</p>
              <p className="text-sm text-muted-foreground">
                {activities?.length === 0 ? 'Start searching and analyzing properties to build your history.' : 'Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredGroups?.map(group => (
                <div key={group?.date}>
                  <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wider">{group?.date}</h2>
                  <div className="space-y-2">
                    {group?.activities?.map((activity, index) => (
                      <ActivityItem key={activity?.id} activity={activity} isLast={index === group?.activities?.length - 1} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default History;
